import { API_HEADERS, DEFAULT_API_BASE_URL, type AuthRealm } from "@sagot/constants";
import createClient from "openapi-fetch";
import { createAuthFetch } from "./auth-fetch";
import { ApiError, httpError, isApiError, transportError } from "./errors";
import type { paths } from "./schema";
import { SessionManager, type ChannelLike, type LockManagerLike, type RefreshMarker } from "./session";

export interface SagotClientOptions {
  realm: AuthRealm;
  baseUrl?: string;
  getWorkspaceId?: () => string | null | undefined;
  onUnauthorized?: () => void;
  /** Transport (defaults to `globalThis.fetch`); always receives a `Request`. */
  fetch?: (request: Request) => Promise<Response>;
  timeoutMs?: number;
  locks?: LockManagerLike | null;
  channel?: ChannelLike | null;
  marker?: RefreshMarker | null;
  readCsrfCookie?: () => string | null;
  now?: () => number;
}

/** Typed OpenAPI client (openapi-fetch) wired to the shared session. */
export function createSagotClient(options: SagotClientOptions) {
  const baseUrl = options.baseUrl ?? DEFAULT_API_BASE_URL;
  const fetchImpl = options.fetch ?? ((request: Request) => globalThis.fetch(request));
  const toRequest = (input: RequestInfo | URL, init?: RequestInit) => (input instanceof Request && !init ? input : new Request(input, init));
  const session = new SessionManager({
    realm: options.realm,
    baseUrl,
    fetch: (input, init) => fetchImpl(toRequest(input, init)),
    locks: options.locks,
    channel: options.channel,
    marker: options.marker,
    readCsrfCookie: options.readCsrfCookie,
    now: options.now,
  });
  const authFetch = createAuthFetch({
    session,
    baseUrl,
    getWorkspaceId: options.getWorkspaceId,
    fetch: (input, init) => fetchImpl(toRequest(input, init)),
    timeoutMs: options.timeoutMs,
    onUnauthorized: options.onUnauthorized,
  });
  const api = createClient<paths>({ baseUrl, fetch: authFetch, credentials: "include" });
  return { api, session, baseUrl };
}

export type SagotClient = ReturnType<typeof createSagotClient>;
export type SagotApi = SagotClient["api"];

interface FetchResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

type DataOf<R> = R extends { data: infer D } ? D : R extends { data?: infer D } ? Exclude<D, undefined> : never;

/** Resolves to the typed body of a 2xx response, or throws a normalized `ApiError`. */
export async function unwrap<R extends FetchResult>(pending: Promise<R>): Promise<DataOf<R>> {
  let result: R;
  try {
    result = await pending;
  } catch (error) {
    if (isApiError(error)) throw error;
    throw transportError(error instanceof SyntaxError ? "parse" : "network");
  }
  const { response } = result;
  if (response.ok) return result.data as DataOf<R>;
  throw httpError(response.status, result.error, {
    requestId: response.headers.get(API_HEADERS.requestId) ?? undefined,
    retryAfter: response.headers.get("Retry-After"),
  });
}

export { ApiError };
